import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Key, Eye, EyeOff, LogIn } from 'lucide-react';
import { validatePasswordMatch, validatePassword } from '../lib/validator';
import HelpIcon from '../components/ui/helpIcon';
import LoginApi from './loginApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';

const ResetPassword: React.FC = () => {
  const [login_password, setPassword] = useState<string>('');
  const [login_password_confirmation, setPasswordConfirmation] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Extract token from URL on component mount
  useEffect(() => {
    setErrorMessage('');
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');

    if (!tokenFromUrl) {
      setErrorMessage('Invalid or missing password reset token');
      return;
    }

    // Validate the token whenever it is available from the URL
    const validateToken = async () => {
      try {
        const isValid = await LoginApi.validateResetToken(tokenFromUrl);
        if (!isValid) {
          setErrorMessage('Invalid or expired password reset token');
          return;
        }
        setToken(tokenFromUrl);
      } catch (error) {
        setErrorMessage('An error occurred while validating the token');
      }
    };

    validateToken();
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'login_password') {
      setPassword(value);
    } else if (name === 'login_password_confirmation') {
      setPasswordConfirmation(value);
    }

    // Clear error messages when user types
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'login_password':
        const passwordResult = validatePassword(value);
        if (!passwordResult.isValid) {
          errors.push(passwordResult.error || 'Invalid password');
        }
        break;

      case 'login_password_confirmation':
        const confirmResult = validatePasswordMatch(login_password, value);
        if (!confirmResult.isValid) {
          errors.push(confirmResult.error || 'Passwords do not match');
        }
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');

    // Validate password
    const passwordResult = validatePassword(login_password);
    if (!passwordResult.isValid) {
      setErrorMessage(passwordResult.error || 'Invalid password');
      return false;
    }

    // Validate password confirmation
    const confirmResult = validatePasswordMatch(login_password, login_password_confirmation);
    if (!confirmResult.isValid) {
      setErrorMessage(confirmResult.error || 'Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!token) {
      setErrorMessage('Invalid or missing password reset token');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      await LoginApi.updatePassword(token, login_password);
      setMessage('Password has been successfully reset. You can now log in with your new password.');

      // Reset form fields after successful submission
      setPassword('');
      setPasswordConfirmation('');
    } catch (error) {
      if (error instanceof Error && error.message.includes('expired')) {
        setErrorMessage('This password reset link has expired. Please request a new one.');
      } else if (error instanceof Error && error.message.includes('invalid')) {
        setErrorMessage('This password reset link is invalid. Please request a new one.');
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="container mx-auto max-w-lg px-4 py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-lg px-4 py-6">
      <h2 className="text-2xl font-semibold mb-6">Reset Password</h2>

      {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {message ? (
          <div className="space-y-4">
            <Alert variant="success">{message}</Alert>
            <Button type="button" onClick={navigateToLogin} size="medium">
              <LogIn size={16} />
              Log In
            </Button>
          </div>
        ) : (
          <>
            {/* New Password */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="login_password">New Password:</Label>
                  <span className="text-red-600 font-semibold">*</span>
                </div>
                <HelpIcon message="Password must be at least 8 characters long, including both lowercase and uppercase letters, at least one number, and at least one special character" />
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="login_password"
                  name="login_password"
                  value={login_password}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Password Confirmation */}
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="login_password_confirmation">Password Confirmation:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Input
                type="password"
                id="login_password_confirmation"
                name="login_password_confirmation"
                value={login_password_confirmation}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button type="submit" size="medium" disabled={isLoading}>
                <Key size={16} />
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default ResetPassword;